<template>
  <AuthGuard>
    <v-container>
      <PageHeader title="Usage" :showBack="true" back-to="/customers">
        <v-btn color="primary" :loading="loading" :disabled="!isRangeValid" @click="loadUsage">
          Apply range
        </v-btn>
      </PageHeader>

      <v-row class="mb-4" dense align="end">
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="startMonth"
            :items="startAutocompleteItems"
            item-title="title"
            item-value="value"
            label="Start month"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            prepend-inner-icon="mdi-calendar-month"
            clearable
            :menu-props="{ maxHeight: 320 }"
            @update:menu="onStartMenuToggle"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="endMonth"
            :items="endAutocompleteItems"
            item-title="title"
            item-value="value"
            label="End month"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            prepend-inner-icon="mdi-calendar-month"
            clearable
            :menu-props="{ maxHeight: 320 }"
            @update:menu="onEndMenuToggle"
          />
        </v-col>
      </v-row>

      <v-alert v-if="rangeError" type="warning" class="mb-4" variant="tonal">
        {{ rangeError }}
      </v-alert>

      <v-alert v-if="errorMessage" type="error" class="mb-4" variant="tonal">
        {{ errorMessage }}
      </v-alert>

      <v-row v-if="usage" class="mb-4" dense>
        <v-col cols="12" sm="6">
          <v-card variant="tonal">
            <v-card-text>
              <div class="text-caption text-medium-emphasis">Bedrock input tokens</div>
              <div class="text-h6">{{ formatNumber(inputTotal, 0) }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card variant="tonal">
            <v-card-text>
              <div class="text-caption text-medium-emphasis">Bedrock output tokens</div>
              <div class="text-h6">{{ formatNumber(outputTotal, 0) }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="pa-0" elevation="2" variant="text">
        <v-card-text class="pa-4">
          <Loading v-if="loading" />
          <div v-else class="card-chart-container">
            <VueApexCharts
              width="100%"
              height="100%"
              type="line"
              :options="apexChartOptions"
              :series="chartSeries"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </AuthGuard>
</template>

<script setup lang="ts">
import { fetchUsageRange } from "@/actions/Usage";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import Loading from "@/components/Widgets/Loading.vue";
import PageHeader from "@/components/PageHeader.vue";
import { formatNumber } from "@/utils";
import type { ApexOptions } from "apexcharts";
import VueApexCharts from "vue3-apexcharts";
import type { IApp } from "@/types";
import _ from "lodash";
import moment from "moment";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

interface MonthItem {
  title: string;
  value: string;
}

const MIN_MONTH_YEAR = 1990;

const monthItems = ref<MonthItem[]>([]);
const isPrependingOlder = ref(false);

const startMonth = ref(moment().subtract(5, "months").format("YYYY-MM"));
const endMonth = ref(moment().format("YYYY-MM"));
const loading = ref(false);
const errorMessage = ref("");
const usage = ref<IApp.IUsageRangeResponse | null>(null);

let startListScrollEl: HTMLElement | null = null;
let endListScrollEl: HTMLElement | null = null;
let startScrollHandler: ((e: Event) => void) | null = null;
let endScrollHandler: ((e: Event) => void) | null = null;

function buildMonthRangeInclusive(from: moment.Moment, to: moment.Moment): MonthItem[] {
  const out: MonthItem[] = [];
  const cur = from.clone().startOf("month");
  const endM = to.clone().startOf("month");
  while (cur.isSameOrBefore(endM, "month")) {
    out.push({
      title: cur.format("MMMM YYYY"),
      value: cur.format("YYYY-MM")
    });
    cur.add(1, "month");
  }
  return out;
}

function initMonthItems() {
  const from = moment().subtract(5, "years").startOf("month");
  const to = moment().startOf("month");
  monthItems.value = buildMonthRangeInclusive(from, to);
}

function isScrollableEl(el: HTMLElement): boolean {
  const st = getComputedStyle(el);
  const oy = st.overflowY;
  const allowsY = oy === "auto" || oy === "scroll" || oy === "overlay" || st.overflow === "auto";
  return allowsY && el.scrollHeight > el.clientHeight + 2;
}

function findScrollElInsideMenu(root: HTMLElement): HTMLElement | null {
  if (isScrollableEl(root)) return root;
  for (const el of root.querySelectorAll<HTMLElement>("*")) {
    if (isScrollableEl(el)) return el;
  }
  const list = root.querySelector(".v-list") as HTMLElement | null;
  return list;
}

function findLatestAutocompleteMenuRoot(): HTMLElement | null {
  const contents = document.querySelectorAll(".v-overlay__content");
  for (let i = contents.length - 1; i >= 0; i--) {
    const c = contents[i] as HTMLElement;
    if (c.querySelector(".v-list, [role='listbox']")) return c;
  }
  return null;
}

function findAutocompleteMenuScrollEl(): HTMLElement | null {
  const root = findLatestAutocompleteMenuRoot();
  if (!root) return null;
  return findScrollElInsideMenu(root);
}

async function prependOneBatch(scrollEl: HTMLElement): Promise<boolean> {
  if (monthItems.value.length === 0) return false;
  const firstYm = monthItems.value[0].value;
  const oldest = moment(firstYm, "YYYY-MM", true);
  if (!oldest.isValid()) return false;
  const earliestAllowed = moment({ year: MIN_MONTH_YEAR, month: 0, day: 1 }).startOf("month");
  if (oldest.clone().subtract(1, "month").isBefore(earliestAllowed, "month")) return false;

  const batch = 24;
  const rows: MonthItem[] = [];
  const cur = oldest.clone().subtract(1, "month");
  for (let i = 0; i < batch; i++) {
    if (cur.isBefore(earliestAllowed, "month")) break;
    rows.unshift({
      title: cur.format("MMMM YYYY"),
      value: cur.format("YYYY-MM")
    });
    cur.subtract(1, "month");
  }
  if (!rows.length) return false;

  const prevScrollHeight = scrollEl.scrollHeight;
  monthItems.value = [...rows, ...monthItems.value];
  await nextTick();
  await nextTick();
  scrollEl.scrollTop += scrollEl.scrollHeight - prevScrollHeight;
  return true;
}

async function prependOlderMonths(scrollEl: HTMLElement) {
  if (isPrependingOlder.value) return;
  isPrependingOlder.value = true;
  try {
    let iteration = 0;
    while (iteration < 12 && scrollEl.scrollTop <= 120) {
      iteration++;
      const extended = await prependOneBatch(scrollEl);
      if (!extended) break;
      if (scrollEl.scrollTop > 120) break;
    }
  } finally {
    isPrependingOlder.value = false;
  }
}

function createScrollHandler(scrollEl: HTMLElement) {
  return () => {
    if (scrollEl.scrollTop <= 120 && !isPrependingOlder.value) {
      void prependOlderMonths(scrollEl);
    }
  };
}

function attachMenuScrollListener(which: "start" | "end") {
  if (which === "start") detachStartScroll();
  else detachEndScroll();

  let attempts = 0;
  const maxAttempts = 48;

  const tryAttach = () => {
    attempts++;
    const scrollEl = findAutocompleteMenuScrollEl();
    if (!scrollEl && attempts < maxAttempts) {
      requestAnimationFrame(tryAttach);
      return;
    }
    if (!scrollEl) return;

    const handler = createScrollHandler(scrollEl);
    scrollEl.addEventListener("scroll", handler, { passive: true });

    if (which === "start") {
      startListScrollEl = scrollEl;
      startScrollHandler = handler;
    } else {
      endListScrollEl = scrollEl;
      endScrollHandler = handler;
    }
  };

  requestAnimationFrame(tryAttach);
}

function detachStartScroll() {
  if (startListScrollEl && startScrollHandler) {
    startListScrollEl.removeEventListener("scroll", startScrollHandler);
  }
  startListScrollEl = null;
  startScrollHandler = null;
}

function detachEndScroll() {
  if (endListScrollEl && endScrollHandler) {
    endListScrollEl.removeEventListener("scroll", endScrollHandler);
  }
  endListScrollEl = null;
  endScrollHandler = null;
}

function onStartMenuToggle(open: boolean) {
  detachStartScroll();
  if (!open) return;
  nextTick(() => {
    requestAnimationFrame(() => {
      attachMenuScrollListener("start");
    });
  });
}

function onEndMenuToggle(open: boolean) {
  detachEndScroll();
  if (!open) return;
  nextTick(() => {
    requestAnimationFrame(() => {
      attachMenuScrollListener("end");
    });
  });
}

const currentMonthYm = computed(() => moment().format("YYYY-MM"));

const startAutocompleteItems = computed(() =>
  monthItems.value.filter((m) => m.value <= endMonth.value && m.value <= currentMonthYm.value)
);

const endAutocompleteItems = computed(() =>
  monthItems.value.filter((m) => m.value >= startMonth.value && m.value <= currentMonthYm.value)
);

watch(startMonth, (v) => {
  if (!v) return;
  const maxYm = currentMonthYm.value;
  if (v > maxYm) {
    startMonth.value = maxYm;
    return;
  }
  if (moment(v, "YYYY-MM").isAfter(moment(endMonth.value, "YYYY-MM"), "month")) {
    endMonth.value = v;
  }
});

watch(endMonth, (v) => {
  if (!v) return;
  const maxYm = currentMonthYm.value;
  if (v > maxYm) {
    endMonth.value = maxYm;
    return;
  }
  if (moment(v, "YYYY-MM").isBefore(moment(startMonth.value, "YYYY-MM"), "month")) {
    startMonth.value = v;
  }
});

const rangeError = computed(() => {
  const s = moment(startMonth.value, "YYYY-MM", true);
  const e = moment(endMonth.value, "YYYY-MM", true);
  if (!startMonth.value || !endMonth.value) return "Select start and end month.";
  if (!s.isValid() || !e.isValid()) return "Select valid months.";
  if (s.isAfter(e, "month")) return "Start month must be on or before end month.";
  return "";
});

const isRangeValid = computed(() => !rangeError.value);

const monthKeys = computed(() => eachMonthInclusive(startMonth.value, endMonth.value));

const chartLabels = computed(() =>
  monthKeys.value.map((m) => moment(m, "YYYY-MM").format("MMM YYYY"))
);

const apexChartOptions = computed<ApexOptions>(() => ({
  chart: {
    zoom: {
      enabled: false
    }
  },
  stroke: {
    curve: "smooth",
    width: 2
  },
  markers: {
    size: 4,
    hover: {
      size: 6
    }
  },
  title: {
    text: "Monthly Bedrock token usage"
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    markers: {
      size: 6,
      strokeWidth: 0
    }
  },
  xaxis: {
    categories: chartLabels.value
  },
  yaxis: {
    labels: {
      formatter(val: string | number) {
        return formatNumber(Number(val));
      }
    }
  },
  tooltip: {
    shared: true,
    intersect: false,
    hideEmptySeries: true,
    y: {
      formatter(val: number) {
        return formatNumber(val, 0);
      }
    }
  }
}));

const chartSeries = computed(() => {
  const keys = monthKeys.value;
  if (!usage.value || keys.length === 0) {
    return [
      { name: "Input tokens", data: [] },
      { name: "Output tokens", data: [] }
    ];
  }
  const grouped = _.groupBy(usage.value.items, "month");
  const inputData = keys.map((m) =>
    _.sumBy(grouped[m] ?? [], (row) => Number(row.bedrockInputTokens) || 0)
  );
  const outputData = keys.map((m) =>
    _.sumBy(grouped[m] ?? [], (row) => Number(row.bedrockOutputTokens) || 0)
  );
  return [
    { name: "Input tokens", data: inputData },
    { name: "Output tokens", data: outputData }
  ];
});

const inputTotal = computed(() => {
  if (!usage.value?.totals) return 0;
  const v = usage.value.totals.bedrockInputTokens;
  return typeof v === "number" ? v : 0;
});

const outputTotal = computed(() => {
  if (!usage.value?.totals) return 0;
  const v = usage.value.totals.bedrockOutputTokens;
  return typeof v === "number" ? v : 0;
});

function eachMonthInclusive(start: string, end: string): string[] {
  const s = moment(start, "YYYY-MM", true);
  const e = moment(end, "YYYY-MM", true);
  if (!s.isValid() || !e.isValid() || s.isAfter(e, "month")) return [];
  const out: string[] = [];
  let cur = s.clone();
  while (cur.isSameOrBefore(e, "month")) {
    out.push(cur.format("YYYY-MM"));
    cur = cur.add(1, "month");
  }
  return out;
}

async function loadUsage() {
  if (!isRangeValid.value) return;
  loading.value = true;
  errorMessage.value = "";
  try {
    usage.value = await fetchUsageRange({
      startMonth: startMonth.value,
      endMonth: endMonth.value
    });
  } catch (err: unknown) {
    usage.value = null;
    errorMessage.value = err instanceof Error ? err.message : "Failed to load usage data.";
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  initMonthItems();
  const maxYm = currentMonthYm.value;
  if (startMonth.value > maxYm) startMonth.value = maxYm;
  if (endMonth.value > maxYm) endMonth.value = maxYm;
  void loadUsage();
});

onBeforeUnmount(() => {
  detachStartScroll();
  detachEndScroll();
});
</script>

<style scoped>
.card-chart-container :deep(.apexcharts-tooltip-marker) {
  display: none !important;
}

.card-chart-container :deep(.apexcharts-tooltip-text) {
  padding-left: 0;
}
</style>
